// senderValidator.js
import * as XLSX from "xlsx";
import JSZip from "jszip";

const DEFAULT_THRESHOLD = 1500;

function norm(str) {
  if (str === undefined || str === null) return "";
  return String(str).trim().toLowerCase();
}

/**
 * Choose which column index to treat as SenderScreenName.
 *
 * Priority:
 *  1) If canonToSrc["SenderScreenName"] exists -> use that header name.
 *  2) Else -> look for literal "SenderScreenName" in header.
 *
 * Returns { index, headerName } where index may be -1 if not found.
 */
function getSenderIndex(header, canonToSrc) {
  const headerNorm = header.map((h) => norm(h));

  let mappedSource = null;
  if (canonToSrc && typeof canonToSrc === "object") {
    mappedSource =
      canonToSrc["SenderScreenName"] ||
      canonToSrc["sender_screen_name"] ||
      null;
  }

  // 1) Try mapped source header (e.g. "Author handle")
  if (mappedSource) {
    const idx = headerNorm.indexOf(norm(mappedSource));
    if (idx !== -1) {
      return { index: idx, headerName: header[idx] ?? mappedSource };
    }
  }

  // 2) Sprinklr exports commonly call this field "User" or
  // "User Screen Name". Keep the canonical fallback as well.
  for (const fallback of ["senderscreenname", "user", "user screen name"]) {
    const fallbackIdx = headerNorm.indexOf(fallback);
    if (fallbackIdx !== -1) {
      return { index: fallbackIdx, headerName: header[fallbackIdx] ?? fallback };
    }
  }

  // Not found
  return { index: -1, headerName: null };
}

/**
 * Process a workbook (XLSX or CSV already parsed by SheetJS).
 * Updates globalSenders, returns { senderColumnFound, distinctAdded, lastHeaderUsed, overThreshold }.
 */
function processWorkbook(workbook, label, globalSenders, canonToSrc, threshold) {
  let senderColumnFound = false;
  let lastHeaderUsed = null;
  let overThreshold = false;

  workbook.SheetNames.forEach((sheetName) => {
    if (overThreshold) return;

    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false });

    if (!rows || rows.length === 0) {
      return;
    }

    // Sprinklr workbooks put a dashboard title and a blank row before the
    // actual header. Find the row containing the mapped sender field instead
    // of assuming row zero is always the header.
    let headerRowIndex = 0;
    const mappedSource =
      canonToSrc && typeof canonToSrc === "object"
        ? canonToSrc["SenderScreenName"] || canonToSrc["sender_screen_name"] || null
        : null;
    const wanted = new Set([norm(mappedSource), "senderscreenname", "user", "user screen name"]);
    for (let i = 0; i < Math.min(rows.length, 10); i++) {
      const candidate = (rows[i] || []).map(norm);
      if (candidate.some((cell) => wanted.has(cell))) {
        headerRowIndex = i;
        break;
      }
    }

    const header = rows[headerRowIndex] || [];
    const { index: senderIdx, headerName } = getSenderIndex(header, canonToSrc);

    if (senderIdx === -1) {
      // no sender column in this sheet
      return;
    }

    senderColumnFound = true;
    lastHeaderUsed = headerName;

    for (let r = headerRowIndex + 1; r < rows.length; r++) {
      const row = rows[r] || [];
      const val = row[senderIdx];

      if (val === undefined || val === null) continue;
      const v = String(val).trim();
      if (!v) continue;

      globalSenders.add(v);

      if (globalSenders.size > threshold) {
        overThreshold = true;
        break;
      }
    }
  });

  return {
    senderColumnFound,
    lastHeaderUsed,
    overThreshold,
  };
}

/**
 * Validate distinct sender counts for a ZIP with CSV / XLSX files.
 */
async function validateZip(arrayBuffer, canonToSrc, threshold, globalSenders) {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const files = Object.values(zip.files);
  let senderColumnFoundAnywhere = false;
  let lastHeaderUsed = null;
  let overThreshold = false;

  for (const entry of files) {
    if (entry.dir) continue;
    const lname = entry.name.toLowerCase();
    if (
      !(
        lname.endsWith(".xlsx") ||
        lname.endsWith(".xls") ||
        lname.endsWith(".csv")
      )
    ) {
      continue;
    }

    const content = await entry.async("arraybuffer");
    const workbook = XLSX.read(content, { type: "array", raw: false });

    const result = processWorkbook(
      workbook,
      `zip:${entry.name}`,
      globalSenders,
      canonToSrc,
      threshold
    );

    if (result.senderColumnFound) {
      senderColumnFoundAnywhere = true;
      if (result.lastHeaderUsed) {
        lastHeaderUsed = result.lastHeaderUsed;
      }
    }

    if (result.overThreshold) {
      overThreshold = true;
      break;
    }
  }

  return {
    senderColumnFoundAnywhere,
    lastHeaderUsed,
    overThreshold,
  };
}

/**
 * Public API:
 * Validate SenderScreenName (or its mapped source) distinct values in a File.
 *
 * @param {File} file - the uploaded file
 * @param {Object|null} canonToSrc - e.g. { SenderScreenName: "Author handle", ... }
 * @param {number} threshold - max allowed distinct senders (default 1500)
 *
 * @returns {Promise<{ ok: boolean, reason?: string, distinctSenders?: number, usedHeader?: string | null }>}
 */
export async function validateSenderDistincts(file, canonToSrc, threshold = DEFAULT_THRESHOLD) {
  if (!file) {
    return { ok: false, reason: "No file provided." };
  }

  const name = file.name.toLowerCase();
  const ext = name.split(".").pop() || "";
  const globalSenders = new Set();

  const mappedSource =
    canonToSrc && typeof canonToSrc === "object"
      ? canonToSrc["SenderScreenName"] || canonToSrc["sender_screen_name"] || null
      : null;

  let senderColumnFoundAnywhere = false;
  let lastHeaderUsed = null;
  let overThreshold = false;

  try {
    const buf = await file.arrayBuffer();

    if (ext === "zip") {
      const zres = await validateZip(buf, canonToSrc, threshold, globalSenders);
      senderColumnFoundAnywhere = zres.senderColumnFoundAnywhere;
      lastHeaderUsed = zres.lastHeaderUsed;
      overThreshold = zres.overThreshold;
    } else if (ext === "xlsx" || ext === "xls" || ext === "csv") {
      const workbook = XLSX.read(buf, { type: "array", raw: false });
      const res = processWorkbook(
        workbook,
        file.name,
        globalSenders,
        canonToSrc,
        threshold
      );
      senderColumnFoundAnywhere = res.senderColumnFound;
      lastHeaderUsed = res.lastHeaderUsed;
      overThreshold = res.overThreshold;
    } else {
      return {
        ok: false,
        reason: `Unsupported file type: .${ext}. Allowed: CSV, XLSX, ZIP.`,
      };
    }

    if (!senderColumnFoundAnywhere) {
      const reason = mappedSource
        ? `Canonical 'SenderScreenName' is mapped to '${mappedSource}', but that column is not present in any sheet/file.`
        : `'SenderScreenName' column is not present in any sheet/file.`;
      return { ok: false, reason };
    }

    const distinct = globalSenders.size;

    if (overThreshold || distinct > threshold) {
      return {
        ok: false,
        reason: `Too many distinct senders: more than ${threshold}. Please narrow your dataset to get deep analysis.`,
        distinctSenders: distinct,
        usedHeader: lastHeaderUsed,
      };
    }

    return {
      ok: true,
      distinctSenders: distinct,
      usedHeader: lastHeaderUsed,
    };
  } catch (err) {
    console.error("Browser sender validation failed:", err);
    return {
      ok: false,
      reason: `Failed to validate senders: ${err?.message || String(err)}`,
    };
  }
}
